import {
    Grid, Tab, Tabs, Typography
} from '@mui/material';
import { ActionButton, CancelButtonGrey } from '@tassat/button';
import {
    ActionTypes,
    TransactionStatusCode,
    TransactionStatusCodeMap,
    doesWorkflowRejectCodeExist
} from '@tassat/common-definitions';
import { CustomerModifyUserReq, UserRecord } from '@tassat/fred-definitions';
import {
    ClientUser as ClientUserIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    Phone as PhoneIcon,
    SsoUser as SSOUserIcon,
    Unlock as UnlockIcon,
    Wallet as WalletIcon
} from '@tassat/icon';
import { TabPanel } from '@tassat/layout';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { FIRM_VIEW_USERS_TAB } from 'Containers/admin/pages/firms/firmView';
import FirmUserWallets from 'Containers/admin/pages/firms/firmView/firmUsers/firmUserDetails/firmUserWallets';
import { LOCK_ICON_COLOR } from 'Lib/constant';
import { FeaturesContext } from 'Lib/context/featuresContext';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole } from 'Selectors/common';
import { getUserById } from 'Selectors/users';
import UserActivityLogGrid from 'SharedComponents/users/userActivityLogGrid';
import UserAuthActions from 'SharedComponents/users/userAuthActions';
import UserStatistics from 'SharedComponents/users/userStatistics';

import { FirmUserDetailsProps } from '.';

export function FirmUserDetails(props: FirmUserDetailsProps) {
    const {
        classes,
        firm,
        match,
        editUser,
        history,
        location,
        isSSOEnabled
    } = props;

    const userRole = useAppSelector(getCurrentRole);
    const user = useAppSelector((state) => getUserById(state, match.params.userId, undefined, undefined, undefined));

    const features = React.useContext(FeaturesContext);
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);
    const [ currentTab, setCurrentTab ] = React.useState<number>(0);

    React.useEffect(() => {
        if (!user || (user && (Object.keys(user).length === 0 || user.IsDeleted))) {
            history.push(`/admin/firms/${firm?.FirmId}`, {
                ...location.state,
                tab: FIRM_VIEW_USERS_TAB
            });
        }
    }, [user]);

    const deleteClientUserValidation = async (user: UserRecord): Promise<boolean> => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: user,
                actionType: ActionTypes.delete,
                confirmTitle: 'Confirm Deleting User',
                confirmBody: `Please confirm deleting the user: ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const deleteClientUser = async (user: UserRecord) => {
        try {
            const sendValues = { ...user };
            const pendingTransactions = {
                Condition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL].value}:requesterId|EQ|${user.Email}`,
                PageNo: 1,
                RecordPerPage: 15,
                Sort: 'auditTime|DESC'
            };

            const res = await Worker.getInstance()
                .getTransactions(pendingTransactions);

            if (res.data.transactions.filter(transaction => transaction.UserId === sendValues.Email).length > 0) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: 'Failed to Delete User',
                        failureBody: `${user.Email} has pending transactions and cannot be deleted.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);

                return;
            }

            const response = await Worker.getInstance()
                .deleteUser(sendValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: 'User Successfully Deleted',
                        successBody: `User ${user.Email} has been successfully deleted.`
                    }
                };
                //No neeed to close alert and form modal as we are redirecting
                history.push(`/admin/firms/${user.FirmId}`, {
                    ...location.state,
                    tab: FIRM_VIEW_USERS_TAB
                });
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: 'Failed to Delete User',
                        failureBody: `Could not delete user ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: 'Failed to Delete User',
                    failureBody: `Something went wrong deleting user ${user.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const freezeClientUserValidation = (user: UserRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: user,
                actionType: user.IsFrozen ? ActionTypes.unfreeze : ActionTypes.freeze,
                confirmTitle: `Confirm ${user.IsFrozen ? 'Unfreezing' : 'Freezing'} User`,
                confirmBody: `Please confirm ${user.IsFrozen ? 'unfreezing' : 'freezing'} user ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const freezeClientUser = async (user: UserRecord) => {
        try {
            // User ID and Email needed for all User updates
            const userUpdateValues: Partial<CustomerModifyUserReq> = {
                UserId: user.UserId,
                Email: user.Email,
                IsFrozen: !user.IsFrozen
            };

            const response = await Worker.getInstance()
                .updateUser(user.FirmId, userUpdateValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${user.IsFrozen ? 'Unfroze' : 'Froze'} User`,
                        successBody: `Successfully ${user.IsFrozen ? 'unfroze' : 'froze'} user ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${user.IsFrozen ? 'Unfreeze' : 'Freeze'} User`,
                        failureBody: `Could not ${user.IsFrozen ? 'unfreeze' : 'freeze'} user ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${user.IsFrozen ? 'Unfreeze' : 'Freeze'} User`,
                    failureBody: `Something went wrong ${user.IsFrozen ? 'unfreeze' : 'freeze'} user ${user.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const lockClientUserValidation = (user: UserRecord): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: user,
                actionType: user.IsLocked ? ActionTypes.unlock : ActionTypes.lock,
                confirmTitle: `Confirm ${user.IsLocked ? 'Unlocking' : 'Locking'} User`,
                confirmBody: `Please confirm ${user.IsLocked ? 'unlocking' : 'locking'} user functionality for ${user.Email}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const lockClientUser = async (user: UserRecord) => {
        try {
            // User ID and Email needed for all User updates
            const userUpdateValues: Partial<CustomerModifyUserReq> = {
                UserId: user.UserId,
                Email: user.Email,
                IsLocked: !user.IsLocked
            };

            const response = await Worker.getInstance()
                .updateUser(user.FirmId, userUpdateValues);

            if (response.data && response.data.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully ${user.IsLocked ? 'Unlocked' : 'Locked'} User`,
                        successBody: `Successfully ${user.IsLocked ? 'unlocked' : 'locked'} user functionality for ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to ${user.IsLocked ? 'Unlock' : 'Lock'} User`,
                        failureBody: `Could not ${user.IsLocked ? 'unlock' : 'lock'} user functionality for ${user.Email}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to ${user.IsLocked ? 'Unlock' : 'Lock'} User`,
                    failureBody: `Something went wrong ${user.IsLocked ? 'unlocking' : 'locking'} user functionality for ${user.Email}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: user,
                setSelectedItem: () => user,
                preActionValidationAndConfig: {
                    [ActionTypes.freeze]: freezeClientUserValidation,
                    [ActionTypes.unfreeze]: freezeClientUserValidation,
                    [ActionTypes.lock]: lockClientUserValidation,
                    [ActionTypes.unlock]: lockClientUserValidation,
                    [ActionTypes.delete]: deleteClientUserValidation
                },
                onConfirm: {
                    [ActionTypes.freeze]: freezeClientUser,
                    [ActionTypes.unfreeze]: freezeClientUser,
                    [ActionTypes.lock]: lockClientUser,
                    [ActionTypes.unlock]: lockClientUser,
                    [ActionTypes.delete]: deleteClientUser
                },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='center'
                spacing={0}
            >
                <Grid
                    className={classes.cancelButton}
                    item
                >
                    <CancelButtonGrey
                        onClick={() => history.push(`/admin/firms/${user.FirmId}`, {
                            ...location.state,
                            tab: FIRM_VIEW_USERS_TAB
                        })}
                    />
                </Grid>
                <Grid
                    className={classes.userContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.userHeaderContainer}
                        container
                        direction='row'
                        spacing={0}
                    >
                        <Grid
                            item
                            xs={10}
                        >
                            <Grid
                                container
                                direction='column'
                                wrap='nowrap'
                            >
                                <Grid
                                    alignItems='center'
                                    className={classes.userDetailContainer}
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography
                                        className={classes.userFullName}
                                        variant='h4'
                                    >{`${user.FirstName} ${user.LastName}`}
                                    </Typography>
                                    {user.IsLocked &&
                                        <LockIcon
                                            className={classes.userStatusIcon}
                                            iconColor={LOCK_ICON_COLOR}
                                            id='userLockedIcon'
                                        />}
                                    {user.IsFrozen &&
                                        <WalletIcon
                                            className={classes.userStatusIcon}
                                            iconColor={LOCK_ICON_COLOR}
                                            id='userFrozenIcon'
                                        />}
                                </Grid>
                                {
                                    user.Email &&
                                    <Grid
                                        container
                                        item
                                        spacing={1}
                                    >
                                        <Grid item>
                                            <EmailIcon id='userDetailsEmail' />
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                className={classes.userDetailText}
                                                variant='body2'
                                            >
                                                {user.Email}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    user.Username &&
                                    <Grid
                                        container
                                        item
                                        spacing={1}
                                    >
                                        <Grid item>
                                            {
                                                isSSOEnabled ?
                                                    <SSOUserIcon id='userDetailsSSoUserId' />
                                                    : <ClientUserIcon id='userDetailsUserId' />
                                            }
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                className={classes.userDetailText}
                                                variant='body2'
                                            >
                                                {user.Username}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    user.Phone &&
                                    <Grid
                                        container
                                        item
                                        spacing={1}
                                    >
                                        <Grid item>
                                            <PhoneIcon id='userDetailsPhone' />
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                className={classes.userDetailText}
                                                variant='body2'
                                            >
                                                {user.Phone}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                        {userRole.IsManageFirmAllowed && (
                        // (mappedUserAccounts.length === 0 || hasMatchingCurrencies)
                        // &&
                            <Grid
                                container
                                direction='row'
                                item
                                spacing={0}
                                wrap='nowrap'
                                xs={2}
                            >
                                <ActionButton
                                    className={classes.actionButton}
                                    disabled={firm?.IsClosed}
                                    handleClick={() => freezeClientUserValidation(user)}
                                    Icon={WalletIcon}
                                    id='firmDisableClientUserButton'
                                    text={user.IsFrozen ? 'Unfreeze' : 'Freeze'}
                                    type='icon'
                                />
                                <ActionButton
                                    className={classes.actionButton}
                                    disabled={firm?.IsClosed}
                                    handleClick={() => lockClientUserValidation(user)}
                                    Icon={user.IsLocked ? UnlockIcon : LockIcon}
                                    id='firmLockClientUserButton'
                                    text={user.IsLocked ? 'Unlock' : 'Lock'}
                                    type='icon'
                                />
                                <ActionButton
                                    className={classes.actionButton}
                                    handleClick={() => editUser(user, () => deleteClientUserValidation)}
                                    Icon={EditIcon}
                                    id='firmEditClientUserButton'
                                    text='Edit'
                                    type='icon'
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Tabs
                        classes={{ indicator: classes.indicator }}
                        className={classes.userTabsContainer}
                        onChange={(e, newTab) => setCurrentTab(newTab)}
                        value={currentTab}
                        variant='fullWidth'
                    >
                        <Tab
                            className={classes.userTab}
                            id='firmUserDetailsTab'
                            label='Details'
                        />
                        <Tab
                            className={classes.userTab}
                            id='firmUserAssociatedAccountsTab'
                            label='Associated Accounts'
                        />
                        <Tab
                            className={classes.userTab}
                            id='firmUserActivityLogTab'
                            label='Activity Log'
                        />
                    </Tabs>
                    <Grid
                        item
                        xs={12}
                    >
                        <TabPanel
                            index={0}
                            value={currentTab}
                        >
                            <>
                                <UserStatistics
                                    user={user}
                                />
                                {userRole.IsManageFirmAllowed && !firm?.IsClosed && (
                                // (mappedUserAccounts.length === 0 || hasMatchingCurrencies)
                                    <UserAuthActions
                                        firmId={user.FirmId}
                                        user={user}
                                    />
                                )}
                            </>
                        </TabPanel>
                        <TabPanel
                            index={1}
                            value={currentTab}
                        >
                            <FirmUserWallets
                                firm={firm}
                                user={user}
                                userRole={userRole}
                            />
                        </TabPanel>
                        <TabPanel
                            index={2}
                            value={currentTab}
                        >
                            <UserActivityLogGrid
                                user={user}
                            />
                        </TabPanel>
                    </Grid>

                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}